import api from 'api'
import { get } from 'utils/lodash'

import { optimisticNoteCreate } from './optimisticNoteCount'

import {
  CACHE_TAG_LOCATION_NOTES_LIST,
  CACHE_TAG_LOCATIONS_NOTES_COUNT,
  CACHE_TAG_ACCOUNT_LOCATION_LIST,
  CACHE_TAG_ACCOUNT_LOCATION_DETAILS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
} from '../cacheTagTypes'

type NoteRecord = Record<string, unknown>
type NotesResponse = { notes: NoteRecord[] }
type NoteBody = { note: string }

type LocationNoteArg = { id: string; noteId: string; data?: NoteBody }

// The route board's note badge counts location notes, so every write has to move it too.
const writeInvalidatesTags = (id: string) => [
  { type: CACHE_TAG_LOCATION_NOTES_LIST, id },
  { type: CACHE_TAG_ACCOUNT_LOCATION_DETAILS, id },
  CACHE_TAG_LOCATIONS_NOTES_COUNT,
  CACHE_TAG_ACCOUNT_LOCATION_LIST,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
]

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    getAllLocationNotes: builder.query<NotesResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/api/v1/core/location/${id}/notes`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [CACHE_TAG_LOCATION_NOTES_LIST, { type: CACHE_TAG_LOCATION_NOTES_LIST, id }],
    }),
    getLocationNoteDetails: builder.query<NoteRecord, { locationId: string; noteId: string }>({
      query: ({ locationId, noteId }) => ({
        url: `/api/v1/core/location/${locationId}/notes/${noteId}`,
        method: 'GET',
      }),
    }),
    getLocationNoteHistory: builder.query<NotesResponse, { id: string; noteId: string }>({
      query: ({ id, noteId }) => ({
        url: `/api/v1/core/location/${id}/notes/${noteId}/history`,
        method: 'GET',
      }),
    }),
    createLocationNote: builder.mutation<NoteRecord, { id: string; data: NoteBody }>({
      query: ({ id, data }) => ({
        url: `/api/v1/core/location/${id}/notes`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
      // Every stop backed by this location counts the note, so the badges move now and the
      // invalidation above reconciles afterwards.
      onQueryStarted: optimisticNoteCreate('locationNoteCount', id => stop => get(stop, 'locationId', '') === id),
    }),
    updateLocationNote: builder.mutation<NoteRecord, LocationNoteArg>({
      query: ({ id, noteId, data }) => ({
        url: `/api/v1/core/location/${id}/notes/${noteId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
    }),
    deleteLocationNote: builder.mutation<void, { id: string; noteId: string }>({
      query: ({ id, noteId }) => ({
        url: `/api/v1/core/location/${id}/notes/${noteId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
    }),
    patchLocationNote: builder.mutation<NoteRecord, LocationNoteArg>({
      query: ({ id, noteId, data }) => ({
        url: `/api/v1/core/location/${id}/notes/${noteId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => writeInvalidatesTags(id),
    }),
  }),
})

export const {
  useLazyGetAllLocationNotesQuery,
  useLazyGetLocationNoteDetailsQuery,
  useLazyGetLocationNoteHistoryQuery,
  useCreateLocationNoteMutation,
  useUpdateLocationNoteMutation,
  useDeleteLocationNoteMutation,
  usePatchLocationNoteMutation,
} = extendedApi
